import { useLazyQuery } from '@apollo/client';
import _, { get, isEmpty, uniqBy, isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { GRAINID_GRAIN_MAP, PRICING_MENU_ITEMS, PRICING_TYPE_MAP } from '../../helpers/pricing';
import {
    applyActivityRules,
    applyDimensionRules,
    filterDimensions,
    getInheritedRules,
    groupDatasetRulesByType,
    mapPricingGrain,
} from '../../helpers/rules';
import { GET_ACTIVITIES_BY_GRAIN } from '../../queries/activities';
import { GET_GRAIN_HIERARCHY, GET_PRICING_GRAINS } from '../../queries/grainHierarchy';
import ActivityMappingAccordion from '../components/ActivityMappingAccordion';
import AliasingAccordion from '../components/AliasingAccordion';
import PricingAccordion from '../components/PricingAccordion';

const ALLOWED_GRAINS = {
    account: {
        defaultFieldName: 'account_name_alias',
        gwtaxFieldName: 'account_name_gwtax',
        idColumn: 'account_id',
    },
    campaign_group: {
        defaultFieldName: 'campaign_group_name_alias',
        gwtaxFieldName: 'campaign_group_name_gwtax',
        idColumn: 'campaign_group_id',
    },
    campaign: {
        defaultFieldName: 'campaign_name_alias',
        gwtaxFieldName: 'campaign_name_gwtax',
        idColumn: 'campaign_id',
    },
    adgroup: {
        defaultFieldName: 'adgroup_name_alias',
        gwtaxFieldName: 'adgroup_name_gwtax',
        idColumn: 'adgroup_id',
    },
    placement: {
        defaultFieldName: 'placement_name_alias',
        gwtaxFieldName: 'placement_name_gwtax',
        idColumn: 'placement_id',
    },
    line_item: {
        defaultFieldName: 'line_item_name_alias',
        gwtaxFieldName: 'line_item_name_gwtax',
        idColumn: 'line_item_id',
    },
    ad: {
        defaultFieldName: 'ad_name_alias',
        gwtaxFieldName: 'ad_name_gwtax',
        idColumn: 'ad_id',
    },
    creative: {
        defaultFieldName: 'creative_name_alias',
        gwtaxFieldName: 'creative_name_gwtax',
        idColumn: 'creative_id',
    },
    keyword: {
        defaultFieldName: 'keyword_alias',
        gwtaxFieldName: 'keyword_gwtax',
        idColumn: 'keyword_id',
    },
};

const HIERARCHY_ORDER = Object.keys(ALLOWED_GRAINS);

export const createUpdatedDimensions = (
    datasetRules,
    oldDimensions,
    data,
    resetSaved,
    fetchedGrains,
    affectedRows,
) => {
    if (_.isEmpty(affectedRows) && _.isEmpty(data?.getGrainHierarchy)) {
        return oldDimensions;
    }

    const isFullUpdate = !_.isEmpty(data?.getGrainHierarchy) && _.isEmpty(affectedRows);

    const newDimensions =
        data?.getGrainHierarchy?.reduce((dimensionList, dimension) => {
            const { type, id, name, platform, platform_grain_id, path, auto_alias } = dimension;

            if (type) {
                if (!dimensionList[type]) {
                    dimensionList[type] = [];
                }

                const newDimension = {
                    id,
                    grainID: platform_grain_id,
                    name: name || platform_grain_id,
                    auto_alias: auto_alias,
                    platform,
                    path,
                };

                if (!dimensionList.platform) {
                    dimensionList.platform = [];
                }

                if (!dimensionList.platform.find(p => p.id === platform)) {
                    dimensionList.platform.push({
                        id: platform,
                        name: platform,
                        platform,
                    });
                }

                dimensionList[type].push(newDimension);
            }

            return dimensionList;
        }, {}) || [];

    const newDimensionsKeys = Object.keys(newDimensions).reduce((acc, dim) => {
        acc[dim] = true;
        return acc;
    }, {});

    if (Object.keys(newDimensionsKeys).length) {
        fetchedGrains.current = {
            ...fetchedGrains.current,
            ...newDimensionsKeys,
        };
    }

    const allDimensions = {
        ...oldDimensions,
        ...newDimensions,
    };

    const filteredDimensions = filterDimensions(
        allDimensions,
        affectedRows,
        isFullUpdate,
        HIERARCHY_ORDER,
    );

    let affectedDimsArray;

    if (isFullUpdate) {
        affectedDimsArray = Object.entries(filteredDimensions?.unaffected);
    } else {
        affectedDimsArray = Object.entries(filteredDimensions?.affected);
    }

    const datasetRulesByType = groupDatasetRulesByType(
        datasetRules,
        filteredDimensions,
        fetchedGrains,
        isFullUpdate,
    );

    console.time('updateDimensions', allDimensions);

    // get subset of dimensions based on affected rows
    const updatedDimensions =
        affectedDimsArray.reduce((dims, [key, values]) => {
            dims[key] = values.map(value => {
                const rules = applyDimensionRules(datasetRulesByType, value.grainID);

                const inheritedRules = getInheritedRules(value.path, datasetRulesByType);

                return {
                    ...value,
                    inheritedRules,
                    ...rules,
                };
            });
            return dims;
        }, {}) || {};

    console.timeEnd('updateDimensions');

    let blendedDimensions;

    if (_.isEmpty(filteredDimensions?.affected)) {
        blendedDimensions = updatedDimensions;
    } else {
        blendedDimensions = Object.keys(filteredDimensions?.unaffected).reduce((acc, key) => {
            acc[key] = [
                ...(filteredDimensions?.unaffected[key] || []),
                ...(updatedDimensions[key] || []),
            ];
            return acc;
        }, {});
    }

    blendedDimensions.platform = uniqBy(
        [...(oldDimensions?.platform || []), ...(newDimensions?.platform || [])],
        'id',
    );
    // allow for reset of saved state on first load of dimensions data, otherwise block or it will overwrite saved state on every change
    if (!oldDimensions?.platform?.length || !oldDimensions?.campaign?.length) {
        resetSaved({ dimensions: blendedDimensions });
    }

    return blendedDimensions;
};

const DatasetRules = ({
    datasetRules,
    setDatasetRules,
    datasetAssets,
    accountMap,
    dimensions,
    datasetMeta,
    setDimensions,
    activities,
    setActivities,
    pricingRules,
    setPricingRules,
    excludeFinancialColumns,
    setDatasetMeta,
    setLoading,
    resetSaved,
}) => {
    const [refreshGrainHierarchy, { loading: loadingDimensions }] =
        useLazyQuery(GET_GRAIN_HIERARCHY);
    const [getActivities, { loading: loadingActivities, data: activitiesData }] =
        useLazyQuery(GET_ACTIVITIES_BY_GRAIN);
    const [refreshPricingGrains, { loading: loadingPricingGrains, data: pricingGrainsData }] =
        useLazyQuery(GET_PRICING_GRAINS);

    const [showDetails, setShowDetails] = useState(!!accountMap?.length);
    const [assets, setAssets] = useState(datasetAssets);
    const [currentDimension, setCurrentDimension] = useState('campaign');
    const [currentDimensionActivities, setCurrentDimensionActivities] = useState('account');
    const [pricingDimensions, setPricingDimensions] = useState([]);
    const [loadingRuleUpdate, setLoadingRuleUpdate] = useState(false);
    const datasetRulesRef = useRef();

    // affectedRows: {'campaign': [rowid1,rowid2,rowid3]}
    const [affectedRows, setAffectedRows] = useState({});
    const fetchedGrains = useRef([]);
    const abortControllerRef = useRef({});

    // tech debt to consolidate pricing rules into the dataset rules object.  This is a temporary measure to
    // create the initial pricing rules.
    const hasInitializedPricingRules = useRef(false);
    const formatAccountMap = () => {
        return accountMap.map(({ platform_grain_id, platform }) => ({
            grainID: platform_grain_id,
            platform,
        }));
    };

    const updateDimensions = data => {
        setDimensions(oldDimensions => {
            return createUpdatedDimensions(
                datasetRules,
                oldDimensions,
                data,
                resetSaved,
                fetchedGrains,
                affectedRows,
            );
        });
    };

    const updateActivities = () =>
        setActivities(oldActivities => {
            const newActivities = (activitiesData?.getActivitiesByGrain || []).map(
                (activity, index) => {
                    const newActivity = {
                        ...activity,
                        id: `${activity.grainID}-${activity.activityID}`,
                        index,
                    };

                    const activityRules = applyActivityRules(datasetRules, activity, newActivity);

                    return { ...newActivity, ...activityRules };
                },
            );
            // allow for reset of saved state on first load of activities data, otherwise block or it will overwrite saved state on every change
            if (!oldActivities?.length) {
                resetSaved({ activities: newActivities });
            }

            return newActivities;
        });

    const updatePricingDimensions = () => {
        if (!loadingPricingGrains) {
            setPricingDimensions(() => ({
                account: mapPricingGrain(pricingGrainsData?.pricingGrains?.account, datasetRules),
                campaign: mapPricingGrain(pricingGrainsData?.pricingGrains?.campaign, datasetRules),
                placement: mapPricingGrain(
                    pricingGrainsData?.pricingGrains?.placement,
                    datasetRules,
                ),
            }));
        }
    };

    const refetchGrains = (accountMap, abortController) => {
        if (accountMap?.length && !fetchedGrains.current[currentDimension]) {
            refreshGrainHierarchy({
                variables: { accountMap, grainTypes: [currentDimension] },
                context: {
                    fetchOptions: {
                        signal: abortController.signal,
                    },
                },
            }).then(({ data }) => {
                return updateDimensions(data);
            });
        }
    };

    const refetchActivities = (accountMaps, grain, abortController) => {
        if (accountMaps?.length) {
            getActivities({
                variables: { accountMaps, grain },
                context: {
                    fetchOptions: {
                        signal: abortController?.signal,
                    },
                },
            });
        }
    };

    const refetchPricingGrains = (accountMaps, abortController) => {
        if (accountMaps?.length) {
            refreshPricingGrains({
                variables: { accountMap: accountMaps },
                context: {
                    fetchOptions: {
                        signal: abortController?.signal,
                    },
                },
            });
        }
    };

    useEffect(() => {
        fetchedGrains.current = {};

        if (!!accountMap.length) {
            if (abortControllerRef.current) {
                Object.values(abortControllerRef.current).forEach(controller => controller.abort());
            }

            const refetchGrainsController = new window.AbortController();
            const refetchActivitiesController = new window.AbortController();
            const refetchPricingRulesController = new window.AbortController();

            abortControllerRef.current = {
                refetchGrainsController,
                refetchActivitiesController,
                refetchPricingRulesController,
            };

            const accountMap = formatAccountMap();
            refetchGrains(accountMap, refetchGrainsController);
            refetchActivities(accountMap, currentDimensionActivities, refetchActivitiesController);
            refetchPricingGrains(accountMap, refetchPricingRulesController);
        }
    }, [accountMap]);

    useEffect(() => {
        updateActivities();
    }, [activitiesData, datasetRules]);

    useEffect(() => {
        setLoadingRuleUpdate(!loadingRuleUpdate);

        if (!isEqual(datasetRulesRef.current, datasetRules)) {
            if (!loadingRuleUpdate) {
                setLoadingRuleUpdate(!loadingRuleUpdate);
            }
            datasetRulesRef.current = datasetRules;
            updateDimensions();
            updatePricingDimensions();
            initializePricingRulesAfterLoad();

            if (!isEmpty(affectedRows)) {
                setAffectedRows({});
                if (loadingRuleUpdate) {
                    setLoadingRuleUpdate(!loadingRuleUpdate);
                }
            }
        }
    }, [datasetRules, affectedRows]);

    useEffect(updatePricingDimensions, [pricingGrainsData]);

    useEffect(() => {
        refetchGrains(formatAccountMap(), abortControllerRef.current?.refetchGrainsController);
    }, [currentDimension]);

    useEffect(
        () =>
            refetchActivities(
                formatAccountMap(),
                currentDimensionActivities,
                abortControllerRef.current?.refetchActivitiesController,
            ),
        [currentDimensionActivities],
    );

    // Tech debt SOL-1532: remove this after consolidating rules into dataset rules object.
    const initializePricingRulesAfterLoad = () => {
        if (!hasInitializedPricingRules.current && datasetRules.length) {
            hasInitializedPricingRules.current = true;
            setPricingRules(() => {
                const updatedPricingRules = datasetRules
                    .filter(rule => rule.type === 'calculation')
                    .reduce((acc, rule) => {
                        const grain = get(GRAINID_GRAIN_MAP, rule.config?.grain, null);

                        const grains =
                            grain &&
                            pricingDimensions &&
                            pricingDimensions[grain] &&
                            rule.config.grain_ids.reduce((grainsList, grainID) => {
                                const pricingGrain = pricingDimensions[grain].find(
                                    g => g.platform_grain_id === grainID,
                                );

                                if (pricingGrain) {
                                    grainsList.push(pricingGrain);
                                }

                                return grainsList;
                            }, []);

                        const config = {
                            ...rule.config,
                            id: rule.config.id || uuidv4(),
                            calculation_type: PRICING_MENU_ITEMS[rule.config.calculation_type],
                            grains,
                        };

                        if (!acc[grain]) {
                            acc[grain] = [];
                        }

                        // margin90 and marginprisma are considered the same calculation type when comparing rules. If calc type is
                        // MarginG90 or MarginPrisma -> map to Margin otherwise use the original value (Fee or Markup)
                        const config_calc_type = get(
                            PRICING_TYPE_MAP,
                            config.calculation_type,
                            config.calculation_type,
                        );

                        // check for duplicate rules aka same calc type, start_date, all grain_ids are the same as current rule
                        const duplicate = acc[grain]?.find(r => {
                            const r_calc_type = get(
                                PRICING_TYPE_MAP,
                                r?.calculation_type,
                                r?.calculation_type,
                            );
                            return (
                                r_calc_type === config_calc_type &&
                                r?.start_date === config?.start_date &&
                                r?.value === config?.value &&
                                r?.grain_ids.every(grainID => config?.grain_ids?.includes(grainID))
                            );
                        });

                        if (!duplicate) {
                            acc[grain].push(config);
                        }

                        return acc;
                    }, {});
                resetSaved({ pricingRules: updatedPricingRules });

                return updatedPricingRules;
            });
        }
    };

    useEffect(() => setShowDetails(!!accountMap.length), [accountMap]);

    useEffect(() => setAssets(a => _.uniqBy([...a, ...datasetAssets], 'key')), [datasetAssets]);

    useEffect(() => {
        setLoading(loadingDimensions || loadingActivities || loadingPricingGrains);
    }, [loadingDimensions, loadingActivities, loadingPricingGrains]);

    const onUpload = asset => {
        setAssets(initalAssets => {
            return _.uniqBy([...initalAssets, { key: asset.key, url: asset.url }], 'key');
        });
    };

    const updateAffectedRows = (currentDimension, rowIds) => {
        setAffectedRows({ [currentDimension]: rowIds });
    };

    return (
        <>
            {showDetails && (
                <>
                    <AliasingAccordion
                        datasetMeta={datasetMeta}
                        assets={assets}
                        accountMap={accountMap}
                        dimensions={dimensions}
                        setDimensions={setDimensions}
                        loading={loadingDimensions || loadingRuleUpdate}
                        onUpload={onUpload}
                        currentDimension={currentDimension}
                        setCurrentDimension={setCurrentDimension}
                        affectedRows={affectedRows}
                        updateAffectedRows={updateAffectedRows}
                        setDatasetRules={setDatasetRules}
                    />
                    <ActivityMappingAccordion
                        datasetMeta={datasetMeta}
                        activities={activities}
                        setActivities={setActivities}
                        loading={loadingActivities}
                        setDatasetRules={setDatasetRules}
                        currentDimension={currentDimensionActivities}
                        updateAffectedRows={updateAffectedRows}
                        setCurrentDimension={setCurrentDimensionActivities}
                        allowedGrains={Object.keys(ALLOWED_GRAINS)}
                    />
                    <PricingAccordion
                        dimensions={pricingDimensions}
                        loading={loadingPricingGrains}
                        pricingRules={pricingRules}
                        setDatasetRules={setDatasetRules}
                        setPricingRules={setPricingRules}
                        excludeFinancialColumns={excludeFinancialColumns}
                        datasetMeta={datasetMeta}
                        setDatasetMeta={setDatasetMeta}
                    />
                </>
            )}
        </>
    );
};

export default DatasetRules;
